import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Table from "../../../../../core/pagination/datatable";
import CommonFooter from "../../../../../core/common/footer/commonFooter";
import { Edit, RotateCcw } from "feather-icons-react/build/IconComponents";
import { ROLL_CALL_API_BASE_URL } from "../../RollCallConfig/RollCallConfig";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../../../../core/img/imagewithbasebath";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { Spin } from "antd";
import EditShift from "./EditShift";
import AddShift from "./AddShift";

const RollcallShifts = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const formatWorkMinutes = (minutes) => {
    if (!minutes || isNaN(minutes) || minutes <= 0) return "-";

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) {
      return `${mins} Mins`;
    } else if (mins === 0) {
      return `${hours}:00 Hrs`;
    } else {
      return `${hours} : ${mins} Mins`;
    }
  };

  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [perm, setPerm] = useState({
    Read: false,
    Write: false,
    Delete: false,
    Export: false,
  });

  const [editRecord, setEditRecord] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("rollcall-permissions");
    if (!stored) return;

    const map = JSON.parse(stored);
    const path = location.pathname.slice(1);
    const perms = map[path];
    if (!perms) {
      navigate("/rollcall/dashboard");
      return;
    }
    if (perms) {
      setPerm({
        Read: perms.canRead,
        Write: perms.canWrite,
        Delete: perms.canDelete,
        Export: perms.canExport,
      });
    }
  }, [location]);

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");

    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      navigate("/signin");
    }
  }, [navigate]);

  const fetchShifts = async () => {
    if (!tokenData || !superIdData) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/shifts/getShifts?SuperId=${superIdData}`,

        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );

      const data = await response.json();

      if (data.status) {
        setShifts(data.ResultData || []);
      } else {
        console.log("Error", data.message || "Failed to load shifts", "error");
      }
    } catch (error) {
      console.error("Error fetching shifts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tokenData && superIdData) fetchShifts();
  }, [tokenData, superIdData]);

  const handleEdit = (record) => {
    setEditRecord(record);
    setIsEditOpen(true);
  };

  const handleReload = () => window.location.reload();

  const formatTime = (timeStr) => {
    if (!timeStr) return "N/A";
    const date = new Date(timeStr);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const yesNo = (value) => (value ? "Yes" : "No");

  const columns = [
    {
      title: "S.No",
      dataIndex: "index",
      render: (_, record, index) => index + 1,
      width: 70,
    },
    {
      title: " Name",
      dataIndex: "Name",
      sorter: (a, b) => a.Name?.localeCompare(b.Name),
    },

    {
      title: "Start Time",
      dataIndex: "StartTime",
      render: formatTime,
      sorter: (a, b) => a.StartTime?.localeCompare(b.StartTime),
    },
    {
      title: "End Time",
      dataIndex: "EndTime",
      render: formatTime,
      sorter: (a, b) => a.EndTime?.localeCompare(b.EndTime),
    },
    {
      title: "Work Mins",
      dataIndex: "WorkMins",
      sorter: (a, b) => (a.WorkMins || 0) - (b.WorkMins || 0),
      render: (val) => (
        <span className="fw-medium">{formatWorkMinutes(val)}</span>
      ),
    },
    {
      title: "Start Grace",
      dataIndex: "StartGrace",
      sorter: (a, b) => (a.StartGrace || 0) - (b.StartGrace || 0),
      render: (val) => (
        <span className=" fw-medium">{formatWorkMinutes(val)}</span>
      ),
    },
    {
      title: "Night Shift",
      dataIndex: "IsNightShift",
      sorter: true,
      render: (val) => (
        <>
          {val ? (
            <>
              <i
                className="fa fa-check me-1"
                style={{ color: "#28a745" }}
              ></i>{" "}
            </>
          ) : (
            <>
              <i
                className="fa fa-times me-1"
                style={{ color: "#dc3545" }}
              ></i>{" "}
            </>
          )}
        </>
      ),
    },
    {
      title: "OT Applicable",
      dataIndex: "IsOtApplicable",
      sorter: true,
      render: (val) => (
        <>
          {val ? (
            <>
              <i
                className="fa fa-check me-1 "
                style={{ color: "#28a745" }}
              ></i>{" "}
            </>
          ) : (
            <>
              <i
                className="fa fa-times me-1"
                style={{ color: "#dc3545" }}
              ></i>{" "}
            </>
          )}
        </>
      ),
    },
  ];

  if (perm?.Write) {
    columns.push({
      title: "Action",
      dataIndex: "action",
      align: "center",
      render: (_, record) => (
        <Link onClick={() => handleEdit(record)}>
          <Edit className="feather-edit text-info" />
        </Link>
      ),
    });
  }

  const handleDownloadExcel = () => {
    if (!shifts?.length) return;

    const exportData = shifts.map((item, index) => ({
      "S.No": index + 1,
      "Shift Name": item.Name,
      Code: item.Code,
      "Start Time": formatTime(item.StartTime),
      "End Time": formatTime(item.EndTime),
      "Work Minutes": item.WorkMins,
      "Night Shift": yesNo(item.IsNightShift),
      "OT Applicable": yesNo(item.IsOtApplicable),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Shifts");
    XLSX.writeFile(wb, "Shifts_List.xlsx");
  };

  const generatePDF = () => {
    if (!shifts?.length) return;
    const doc = new jsPDF();
    doc.text("Shifts List", 14, 10);

    const tableColumn = [
      "S.No",
      "Shift Name",
      "Code",
      "Start Time",
      "End Time",
      "Work Mins",
      "Night Shift",
      "OT Applicable",
    ];

    const tableRows = shifts.map((item, index) => [
      index + 1,
      item.Name,
      item.Code,
      formatTime(item.StartTime),
      formatTime(item.EndTime),
      item.WorkMins,
      yesNo(item.IsNightShift),
      yesNo(item.IsOtApplicable),
    ]);

    doc.autoTable({ head: [tableColumn], body: tableRows });
    doc.save("Shifts_List.pdf");
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/rollcall/dashboard">
                      <i className="fas fa-home fa-lg" />
                    </Link>
                  </li>
                  <h4 className="breadcrumb-item" aria-current="page">
                    Shifts
                  </h4>
                </ol>
              </nav>
              <h6>Manage Shifts</h6>
            </div>
          </div>

          <ul className="table-top-head">
            {perm?.Export && (
              <>
                <li>
                  <OverlayTrigger
                    placement="top"
                    overlay={(props) => <Tooltip {...props}>PDF</Tooltip>}
                  >
                    <Link onClick={generatePDF}>
                      <ImageWithBasePath
                        src="assets/img/icons/pdf.svg"
                        alt="pdf"
                      />
                    </Link>
                  </OverlayTrigger>
                </li>
                <li>
                  <OverlayTrigger
                    placement="top"
                    overlay={(props) => <Tooltip {...props}>Excel</Tooltip>}
                  >
                    <Link onClick={handleDownloadExcel}>
                      <ImageWithBasePath
                        src="assets/img/icons/excel.svg"
                        alt="excel"
                      />
                    </Link>
                  </OverlayTrigger>
                </li>
              </>
            )}
            <li onClick={handleReload}>
              <OverlayTrigger
                placement="top"
                overlay={(props) => <Tooltip {...props}>Refresh</Tooltip>}
              >
                <Link>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>

          {perm?.Write && (
            <div className="page-btn mb-3">
              <Link
                to="#"
                className="btn btn-success btn-sm"
                onClick={() => setIsAddOpen(true)} // You can handle Add separately
              >
                <i className="ti ti-circle-plus fs-4"></i>
              </Link>
            </div>
          )}
        </div>

        {perm?.Read ? (
          <div className="card table-list-card">
            <div className="card-body">
              {loading ? (
                <div className="text-center my-5">
                  <Spin size="large" />
                  <p className="mt-2">Loading Shifts...</p>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={shifts}
                  rowKey="Id"
                  pagination={true}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body text-center">
              <h1>You dont have access to this page</h1>
            </div>
          </div>
        )}
      </div>

      <CommonFooter />

      <AddShift
        onSuccess={fetchShifts}
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
      />
      <EditShift
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        record={editRecord}
        onSuccess={fetchShifts}
      />
    </div>
  );
};

export default RollcallShifts;
